import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { signInWithRedirect, signOut } from 'aws-amplify/auth';
import { PagesService } from '../content/pages/pages.service';
import { ToastService } from './toast.service';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FETCH_AUTH_SESSION, FetchAuthSessionFn } from './auth.tokens';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly LOCAL_ACCESS_TOKEN_KEY = 'local_access_token';
  private readonly LOCAL_REFRESH_TOKEN_KEY = 'local_refresh_token';

  constructor(
    private readonly router: Router,
    private readonly pagesService: PagesService,
    private readonly toast: ToastService,
    @Inject(FETCH_AUTH_SESSION)
    private readonly fetchAuthSession: FetchAuthSessionFn
  ) { }

  /**
   * Get authentication token for an interactive caller.
   *
   * Destructive on failure: if a session previously existed but Cognito cannot
   * refresh it, the user is logged out. Background and retry paths must use
   * `peekAuthToken()` instead — see the SSE hardening contract, §3.
   */
  async getAuthToken(): Promise<string | null> {
    return this.readAuthToken(false, true);
  }

  /**
   * Read the authentication token without any chance of logging the user out.
   *
   * For background and retry paths (e.g. the credits SSE reconnect loop), where
   * a transient network failure must not destroy the session. Returns null when
   * no usable token is available; the caller decides what to do about it.
   *
   * @param forceRefresh ask Cognito for a fresh token instead of a cached one.
   *   Ignored under `useLocalAuth`, where the stored access token is re-read.
   */
  async peekAuthToken(forceRefresh = false): Promise<string | null> {
    return this.readAuthToken(forceRefresh, false);
  }

  /**
   * Single token-reading code path. `allowLogout` gates both destructive
   * branches: the missing-token branch and the catch.
   */
  private async readAuthToken(
    forceRefresh: boolean,
    allowLogout: boolean
  ): Promise<string | null> {
    if (environment.useLocalAuth) {
      return localStorage.getItem(this.LOCAL_ACCESS_TOKEN_KEY);
    }

    try {
      const session = await this.fetchAuthSession({ forceRefresh });
      const token = session.tokens?.accessToken?.toString() || null;

      // Session existed (user was logged in) but tokens are gone → session died
      if (!token && this.hasActiveSession()) {
        if (!allowLogout) {
          return null;
        }
        console.warn('Session expired — tokens missing after fetchAuthSession. Forcing logout.');
        await this.logout();
        return null;
      }

      return token;
    } catch (error) {
      console.error('Error fetching token:', error);

      // If user was logged in and token refresh failed, force logout
      if (allowLogout && this.hasActiveSession()) {
        console.warn('Session expired — fetchAuthSession threw. Forcing logout.');
        await this.logout();
      }

      return null;
    }
  }

  /**
   * Returns true when there are signs of a previously active session
   * (i.e. user data in localStorage), which helps distinguish
   * "never logged in" from "session expired".
   */
  private hasActiveSession(): boolean {
    return !!localStorage.getItem('user');
  }

  /**
   * Store local authentication tokens (only used when useLocalAuth is enabled).
   */
  setLocalTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.LOCAL_ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(this.LOCAL_REFRESH_TOKEN_KEY, refreshToken);
  }

  /**
   * Get local refresh token (only used when useLocalAuth is enabled).
   */
  getLocalRefreshToken(): string | null {
    return localStorage.getItem(this.LOCAL_REFRESH_TOKEN_KEY);
  }

  setAuthToken(token: string): void {
    localStorage.setItem('token', token);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refresh');
  }

  setRefreshToken(token: string): void {
    localStorage.setItem('refresh', token);
  }

  async loginWithGoogle(){
    try {
      await signInWithRedirect({ provider: "Google" });
    } catch (err) {
      console.error("Google Sign-in Error:", err);
    }
  }

  async handlePostLoginRedirect(): Promise<void> {
    try {
      // Get user profile and subscription info
      const userProfile = (await lastValueFrom(
        this.pagesService.userInfo()
      )) as any;

      const subscriptions = userProfile?.subscriptions;

      // Check subscription conditions
      const hasNoSubscription =
        !subscriptions || // null or undefined
        (Array.isArray(subscriptions) && subscriptions.length === 0); 

      if (hasNoSubscription) {
        // Redirect to choose plan
        this.router.navigate(["/auth/choose-plan"]);
        return; 
      }

      // If subscription exists → redirect to home
      this.router.navigate(["/home"]);
      this.toast.show("Login successful!", "success");
      
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // On error, redirect to choose plan as fallback
      this.router.navigate(["/auth/choose-plan"]);
    }
  }

  private loggingOut = false;

  /**
   * Unified logout: Amplify signOut → clear storage → navigate to /auth.
   * Safe to call multiple times concurrently (guarded by loggingOut flag).
   */
  async logout(): Promise<void> {
    if (this.loggingOut) return;
    this.loggingOut = true;

    try {
      if (!environment.useLocalAuth) {
        await signOut();
      }
    } catch (err) {
      console.warn('Amplify signOut error (non-fatal):', err);
    } finally {
      localStorage.clear();
      sessionStorage.clear();
      this.loggingOut = false;
      this.router.navigate(['/auth']);
    }
  }
  
}
